import { useEffect, useRef } from 'react';

/**
 * Custom hook for polling a function at specified intervals
 * @param {Function} callback - The function to call on each poll
 * @param {number} interval - Polling interval in milliseconds
 * @param {boolean} enabled - Whether polling should be enabled
 * @param {Array} dependencies - Dependencies that should trigger a reset of the polling
 */
export const usePolling = (callback, interval, enabled = true, dependencies = []) => {
  const intervalRef = useRef(null);
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Start polling
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start new interval
    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, ...dependencies]);

  // Return function to manually stop polling
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Return function to manually start polling
  const startPolling = () => {
    if (!intervalRef.current && enabled) {
      intervalRef.current = setInterval(() => {
        callbackRef.current();
      }, interval);
    }
  };

  return { stopPolling, startPolling };
};
